import classNames from 'classnames';
import React, { useMemo, useRef, useState } from 'react';
import { Token, useApiIntegrations } from 'src/common/api/use-integrations.hook';
import { Button } from 'src/common/button';
import { AddStrongIcon, BoxIntegrationIcon } from 'src/monet/icons';
import { DashboardLibraryContentConnectDrive } from './connect-drive';
import { useCurrentUser } from 'src/store/user';
import { useCurrentOrganization } from 'src/store/organization';
import { useParagon } from 'src/utils/use-paragon.hook';
import { useMount } from 'react-use';
import { functionNoop } from 'src/utils/function/noop';
import { BoxShareLinkInterface } from 'src/common/interfaces/integrations/box.interface';
import OrganizationLink from 'src/common/OrganizationLink';
import { DashboardLibraryContentPickBoxFileModal } from './pick-box-file-modal';

interface Props {
  onSelectFileFromGoogleDrive: (data: { name: string; embedUrl: string }) => void;
  onAddContentClick: () => void;
  onAddBoxContentClick: (url: string) => Promise<void>;
}

export const BoxStatus = {
  Idle: 0,
  Disconnected: 1,
  Connected: 2,
};

export const DashboardLibraryContentAddSection = ({
  onAddContentClick,
  onSelectFileFromGoogleDrive,
  onAddBoxContentClick,
}: Props) => {
  const [googleTokens, setGoogleTokens] = useState<Token[]>([]);
  const [boxStatus, setBoxStatus] = useState<typeof BoxStatus[keyof typeof BoxStatus]>(BoxStatus.Idle);
  const { fetchAccessTokens } = useApiIntegrations();

  const boxFilesModalOpenRef = useRef(functionNoop);

  const { currentUser } = useCurrentUser();
  const { currentOrganization } = useCurrentOrganization();

  const paragon = useParagon(currentUser!, currentOrganization);

  useMount(() => {
    const initParagonBoxIntegration = async () => {
      try {
        await paragon.initialize();
        await paragon.authenticate();
        const isBoxIntegrationActive = paragon.isIntegrationActive('box');
        return isBoxIntegrationActive;
      } catch (err) {
        return false;
      }
    };

    fetchAndSetGoogleTokens();
    initParagonBoxIntegration().then((isActive) => {
      if (isActive) {
        setBoxStatus(BoxStatus.Connected);
      } else {
        setBoxStatus(BoxStatus.Disconnected);
      }
    });
  });

  const fetchAndSetGoogleTokens = () => {
    fetchAccessTokens('google-drive').then(setGoogleTokens);
  };

  const hasTokens = useMemo(() => googleTokens.length > 0, [googleTokens]);

  const isBoxDisconnected = boxStatus === BoxStatus.Disconnected;
  const isBoxConnected = boxStatus === BoxStatus.Connected;

  const createBoxContent = async (link: BoxShareLinkInterface) => {
    return onAddBoxContentClick(link.url);
  };

  const renderBoxOrigin = () => {
    if (isBoxDisconnected) {
      return (
        <OrganizationLink
          className='flex items-center btn-bedrock-secondary px-4 py-1 space-x-1 w-40'
          to='/integrations/box'
        >
          <div className='flex gap-2 items-center text-bedrock-p-strong'>
            <BoxIntegrationIcon className='w-6 h-6 m-auto' />
            <span>Connect box</span>
          </div>
        </OrganizationLink>
      );
    }

    if (isBoxConnected) {
      return (
        <button
          type='button'
          className='flex items-center btn-bedrock-secondary px-4 py-1 space-x-1 w-40'
          onClick={() => {
            boxFilesModalOpenRef.current();
          }}
        >
          <div className='flex gap-2 items-center text-bedrock-p-strong'>
            <BoxIntegrationIcon className='w-6 h-6 m-auto' />
            <span>Pick files</span>
          </div>
        </button>
      );
    }

    return null;
  };

  return (
    <>
      <div
        className={classNames(
          'absolute right-0 top-0 self-start px-10 pt-16 bg-bedrock-light-gray text-bedrock-black rounded-lg border-dashed border-2 w-80',
          {
            'pb-16': !hasTokens,
            'pb-11': hasTokens,
          }
        )}
      >
        <div className='flex flex-col gap-6'>
          <p className='text-bedrock-p'>Add your digital links and documents to Journey or drag and drop your files</p>
          <div className='flex flex-col gap-2'>
            <Button
              className='self-start'
              label='Add Content'
              size='regular'
              variant='secondary'
              icon={<AddStrongIcon />}
              onClick={onAddContentClick}
            />
            <DashboardLibraryContentConnectDrive
              tokens={googleTokens}
              onPickFile={onSelectFileFromGoogleDrive}
              onFetchTokens={fetchAndSetGoogleTokens}
            />
            {renderBoxOrigin()}
            <DashboardLibraryContentPickBoxFileModal openRef={boxFilesModalOpenRef} onBoxFilePick={createBoxContent} />
          </div>
        </div>
      </div>
    </>
  );
};
